import { Component, DestroyRef, inject, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { combineLatest, Observable } from "rxjs";
import {
  LorenMeteringContext,
  METERING_CONTEXT_SHOW_FIELD_MAP,
  METERING_CONTEXT_TRANSLATION_NAMES,
} from "../../enums/loren-metering-context.enum";
import { DeviceItem } from "../asset-form/asset-form.component";
import { cloneAbstractControl, ExtraValidator, matchStrings } from "../../../../common/forms/form.utils";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { map, startWith, take, withLatestFrom } from "rxjs/operators";
import { TranslocoService } from "@jsverse/transloco";
import { DeviceService } from "../../../device/device.service";
import {DialogBase} from "../../../../common/dialog/dialog.base";


export const SensorFormGroupValidators = [
  ExtraValidator.atLeastOneRequired('meteringId', 'sensorId')
];

export interface SensorFormGroup {
  meteringId: FormControl<number>; // Asset metering ID
  sensorId: FormControl<number>;
  description: FormControl<string>;
  meteringContext?: FormControl<LorenMeteringContext>;
}

type SensorItem = DeviceItem['sensors'][number];
type SensorOption = SensorItem & { device?: DeviceItem };

interface AssetSensorDialogData {
  sensor?: FormGroup<SensorFormGroup>;
  autocompleteItems$: Observable<DeviceItem[]>
}

@Component({
  templateUrl: "./asset-sensor-dialog.component.html",
  styleUrls: ["./asset-sensor-dialog.component.css"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AssetSensorDialogComponent extends DialogBase<AssetSensorDialogData, FormGroup<SensorFormGroup>> implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  private readonly deviceService = inject(DeviceService);
  private readonly translocoService = inject(TranslocoService);

  readonly isEdit = !!this.data.sensor;
  readonly sensor = cloneAbstractControl(this.data.sensor ?? new FormGroup<SensorFormGroup>({
    meteringId: this.fb.control(null),
    sensorId: this.fb.control(null),
    description: this.fb.nonNullable.control('', [Validators.required]),
  }, { validators: SensorFormGroupValidators }));

  // Autocomplete controls hold either the typed search text or the picked item.
  readonly deviceControl = new FormControl<DeviceItem | string>('');
  readonly sensorControl = new FormControl<SensorItem | string>('');

  private readonly deviceValue$ = this.deviceControl.valueChanges.pipe(
    startWith(null),
    map(() => this.deviceControl.value),
  );
  private readonly sensorValue$ = this.sensorControl.valueChanges.pipe(
    startWith(null),
    map(() => this.sensorControl.value),
  );

  readonly devices$ = combineLatest([this.data.autocompleteItems$, this.deviceValue$]).pipe(
    map(([devices, value]) => {
      if (typeof value !== 'string' || !value) {
        return devices;
      }

      return devices.filter(device => matchStrings(this.deviceLabel(device), value));
    })
  );

  readonly sensors$ = combineLatest([this.data.autocompleteItems$, this.deviceValue$, this.sensorValue$]).pipe(
    map(([devices, device, value]) => {
      // No device picked yet: offer every device's sensors (labelled with their device),
      // picking one selects its device.
      const sensors: SensorOption[] = typeof device === 'string' || !device
        ? devices.flatMap(device => device.sensors.map(sensor => ({ ...sensor, device })))
        : device.sensors;

      if (typeof value !== 'string' || !value) {
        return sensors;
      }

      return sensors.filter(sensor => matchStrings(this.sensorLabel(sensor), value));
    })
  );

  readonly meteringContexts: LorenMeteringContext[] = [
    LorenMeteringContext.DOOR_WINDOW,
    LorenMeteringContext.VEHICLE_IGNITION,
    LorenMeteringContext.SMOKE,
    LorenMeteringContext.TILT,
    LorenMeteringContext.WATER_LEAK,
    LorenMeteringContext.MOVEMENT,
    LorenMeteringContext.STATE
  ];
  METERING_CONTEXT_TRANSLATION_NAMES = METERING_CONTEXT_TRANSLATION_NAMES;

  constructor() {
    super();
    this.dialogRef.updateSize('800px');
    this.dialogRef.updatePosition()
  }

  readonly displayDevice = (device: DeviceItem | string | null): string =>
    typeof device === 'string' ? device : device ? this.deviceLabel(device) : '';

  readonly displaySensor = (sensor: SensorItem | string | null): string =>
    typeof sensor === 'string' ? sensor : sensor ? this.sensorLabel(sensor) : '';

  ngOnInit(): void {
    this.deviceControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(device => {
        const sensor = this.sensorControl.value;

        if (typeof device !== 'string' && device && typeof sensor !== 'string' && sensor
          && !device.sensors.some(item => item.sensorId === sensor.sensorId)) {
          this.sensorControl.reset('');
        }
      });

    this.sensorControl.valueChanges
      .pipe(withLatestFrom(this.data.autocompleteItems$), takeUntilDestroyed(this.destroyRef))
      .subscribe(([sensor, devices]) => {
        const selected = typeof sensor === 'string' || !sensor ? null : sensor;
        this.sensor.controls.sensorId.setValue(selected?.sensorId ?? null);

        if (!selected) {
          return;
        }

        const device = devices.find(device => device.sensors.some(item => item.sensorId === selected.sensorId));
        this.deviceControl.setValue(device ?? '', { emitEvent: false });
      });

    if (this.isEdit) {
      this.data.autocompleteItems$
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe(devices => this.preselect(devices));
    }

    combineLatest([
      this.deviceService.all,
      this.sensor.valueChanges.pipe(startWith(this.sensor.value)),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([devices, formGroup]) => {
        const sensors = devices.flatMap(device => device.sensors)

        const sensor = sensors.find(sensor => sensor.id === formGroup.sensorId);
        if (!sensor) {
          return;
        }

        const shouldHaveMeteringContext = METERING_CONTEXT_SHOW_FIELD_MAP.get(sensor.type)
        if (formGroup.meteringContext === undefined && shouldHaveMeteringContext) {
          this.sensor.addControl('meteringContext', new FormControl(LorenMeteringContext.DOOR_WINDOW, [Validators.required]))
        }

        if (formGroup.meteringContext !== undefined && !shouldHaveMeteringContext) {
          this.sensor.removeControl('meteringContext');
        }
      });
  }

  onSubmit() {
    if(!this.sensor.valid) {
      return;
    }

    this.dialogRef.close(this.sensor);
  }

  private preselect(devices: DeviceItem[]) {
    const sensorId = this.sensor.controls.sensorId.value;
    const device = devices.find(device => device.sensors.some(sensor => sensor.sensorId === sensorId));
    if (!device) {
      return;
    }

    this.deviceControl.setValue(device, { emitEvent: false });
    this.sensorControl.setValue(device.sensors.find(sensor => sensor.sensorId === sensorId), { emitEvent: false });
  }

  private deviceLabel(device: DeviceItem) {
    return device.imei ? `${device.deviceName} (${device.imei})` : device.deviceName;
  }

  private sensorLabel(sensor: SensorItem) {
    const label = this.translocoService.translate(sensor.translationKey);

    return sensor.mac ? `${label} (${sensor.mac})` : label;
  }
}
